import { AgGridAngular } from "@ag-grid-community/angular";
import {
  ColDef,
  GridApi,
  GridOptions,
  ICellRendererParams,
  IRowNode,
  ValueFormatterParams,
} from "@ag-grid-community/core";

import { DecimalPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import {
  AccountMarketplace,
  KeywordTrackerConfig,
  KeywordTrackingFrequency,
  Marketplace,
  User,
} from "@front/m19-api-client";
import { gridConfig } from "@front/m19-grid-config";
import { SegmentConfigType, SegmentEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, KeywordTrackingService, SegmentService } from "@front/m19-services";
import {
  ActionButton,
  ActionButtonsComponent,
  ConfirmPopupComponent,
  ConfirmPopupData,
  EmptyStateComponent,
  IButtonDirective,
  IFormFieldComponent,
  IGridButtonComponent,
  IIconComponent,
  IInputDirective,
  ISelectMenuImports,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CsvExportService, fieldExtractor, simpleField } from "@m19-board/services/csv-export.service";
import { KeywordTrackerService } from "@m19-board/services/keyword-tracker.service";
import { LoadingService } from "@m19-board/services/loading.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { combineLatest, map, switchMap, tap } from "rxjs";
import { actionColumnProperties } from "../grid-config/grid-columns";

export type KeywordTrackerRow = {
  searchTerm: string;
  sfr: number;
  top10: number;
  top25: number;
  frequency: KeywordTrackingFrequency;
  top10Asins: string[];
  strategyIds: number[];
};

export enum KeywordTrackerPageQueryParams {
  segment = "segment",
}

@UntilDestroy()
@Component({
  templateUrl: "./keyword-tracker-page.component.html",
  styleUrls: ["./keyword-tracker-page.component.scss"],
  imports: [
    TranslocoDirective,
    FormsModule,
    IButtonDirective,
    ExportButtonComponent,
    EmptyStateComponent,
    IInputDirective,
    IFormFieldComponent,
    IIconComponent,
    ISelectMenuImports,
    AgGridAngular,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordTrackerPageComponent implements OnInit {
  private accountSelection = inject(AccountSelectionService);
  private keywordTrackingService = inject(KeywordTrackingService);
  private segmentService = inject(SegmentService);
  private modalServiceV2 = inject(ModalService);
  private csvExportService = inject(CsvExportService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private keywordTrackerService = inject(KeywordTrackerService);
  private toastrService = inject(ToasterService);
  private authService = inject(AuthService);
  private translocoService = inject(TranslocoService);
  private loadingService = inject(LoadingService);

  accountId?: string;
  locale?: string;
  marketplace?: Marketplace;
  organizationId?: number;
  accountMarketplace?: AccountMarketplace;

  readonly KEYWORD_TRACKER_GRID_KEY = "keywordTrackerGrid";
  readonly gridData = signal<KeywordTrackerRow[]>([]);
  private gridApi?: GridApi<KeywordTrackerRow>;
  readonly selectedRows = signal<KeywordTrackerRow[]>([]);

  private readonly keywordSegment = toSignal<SegmentEx[]>(
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      map((segments) =>
        Array.from(segments.values()).filter((s) => s.segmentType === SegmentConfigType.KeywordSegment),
      ),
    ),
  );
  readonly searchQuery = signal<string>("");
  readonly filteredKeywordSegment = computed(
    () => this.keywordSegment()?.filter((s) => s.name.toLowerCase().includes(this.searchQuery().toLowerCase())) ?? [],
  );
  selectedSegments = signal<SegmentEx[]>([]);

  // state
  filter = "";
  segmentFilter: Set<SegmentEx> = new Set();
  readonly isReadOnly = toSignal(this.accountSelection.readOnlyMode$, { initialValue: false });
  segments?: SegmentEx[];

  private colDefs: ColDef<KeywordTrackerRow>[] = [
    {
      field: "searchTerm",
      headerName: this.translocoService.translate("keyword-tracker-page.search_term", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("keyword-tracker-page.search_term"),
      flex: 2,
      filter: "agTextColumnFilter",
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      cellRendererSelector: (params: ICellRendererParams<KeywordTrackerRow>) => ({
        component: IGridButtonComponent,
        params: {
          label: params.value,
          variant: "ghost",
          clickAction: () => this.navigateToDetail(params.data!.searchTerm),
        },
      }),
    },
    {
      field: "sfr",
      headerName: this.translocoService.translate("search-term-ranks.search_frequency_rank", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("search-term-ranks.search_frequency_rank"),
      type: "numericColumn",
      filter: "agNumberColumnFilter",
      sort: "asc",
      valueFormatter: (params: ValueFormatterParams<KeywordTrackerRow, number>) =>
        params.value == null ? "" : (new DecimalPipe(this.locale ?? "en").transform(params.value) ?? ""),
    },
    {
      field: "top10",
      headerName: this.translocoService.translate("keyword-tracker-page.in_top10", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("keyword-tracker-page.in_top10"),
      headerTooltip: this.translocoService.translate("keyword-tracker-page.top10_tooltip"),
      type: "numericColumn",
      filter: "agNumberColumnFilter",
    },
    {
      field: "top25",
      headerName: this.translocoService.translate("keyword-tracker-page.top25", {}, "en"),
      headerValueGetter: () => this.translocoService.translate("keyword-tracker-page.top25"),
      type: "numericColumn",
      filter: "agNumberColumnFilter",
    },
    {
      ...actionColumnProperties<KeywordTrackerRow, string>(),
      cellRendererSelector: (params: ICellRendererParams<KeywordTrackerRow>) => {
        const row = params.data;
        if (!row) return undefined;
        const nextFrequency =
          row.frequency === KeywordTrackingFrequency.daily
            ? this.translocoService.translate("common.hourly")
            : this.translocoService.translate("common.daily");
        const actionButtons: ActionButton[] = [
          {
            icon: "ellipsisVertical",
            subItems: [
              {
                title: `${this.translocoService.translate("keyword-tracker-page.change_frequency")} ${nextFrequency}`,
                onClick: () => this.toggleTrackingFrequency(row),
                disabled: this.isReadOnly(),
              },
              {
                title: this.translocoService.translate("keyword-tracker-page.stop_tracking_search_term"),
                onClick: () => this.stopTracking(row.searchTerm),
                disabled: this.isReadOnly(),
              },
            ],
          },
        ];
        return {
          component: ActionButtonsComponent,
          params: { actionButtons },
        };
      },
    },
  ];

  private readonly basicOpts: GridOptions<KeywordTrackerRow> = gridConfig({ gridKey: this.KEYWORD_TRACKER_GRID_KEY });
  gridOptions: GridOptions<KeywordTrackerRow> = {
    ...this.basicOpts,
    context: { componentParent: this },
    columnDefs: this.colDefs,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: false,
    },
    rowSelection: "multiple",
    suppressRowClickSelection: true,
    sideBar: false,
    enableRangeSelection: false,
    pagination: true,
    paginationPageSize: 25,
    paginationPageSizeSelector: [5, 10, 25, 50],
    onGridReady: (params) => {
      this.gridApi = params.api;
      if (this.basicOpts.onGridReady) this.basicOpts.onGridReady(params);
    },
    onSelectionChanged: (event) => this.selectedRows.set(event.api.getSelectedRows()),
    isExternalFilterPresent: () => this.isSegmentFilterPresent(),
    doesExternalFilterPass: (node: IRowNode<KeywordTrackerRow>) => this.segmentFilterPass(node),
  };

  ngOnInit(): void {
    this.authService.loggedUser$.pipe(untilDestroyed(this)).subscribe((u: User) => {
      this.locale = u.locale;
    });
    this.loadingService.loadingOn();
    this.accountSelection.singleAccountMarketplaceSelection$
      .pipe(
        untilDestroyed(this),
        tap((am: AccountMarketplace) => {
          this.loadingService.loadingOn();
          this.accountId = am.accountId;
          this.marketplace = am.marketplace;
          this.organizationId = am.resourceOrganizationId;
          this.accountMarketplace = am;
          this.gridData.set([]);
        }),
        switchMap((am: AccountMarketplace) =>
          combineLatest<[KeywordTrackerRow[], Map<number, SegmentEx>, ParamMap]>([
            this.keywordTrackerService.keywordTrackerData$,
            this.segmentService.getSegments(am.accountId, am.marketplace),
            this.route.queryParamMap,
          ]),
        ),
      )
      .subscribe(([kwTrackerData, segmentIndex, params]) => {
        this.gridData.set(kwTrackerData);
        this.segments = Array.from(segmentIndex.values()).filter(
          (s) => s.segmentType == SegmentConfigType.KeywordSegment,
        );

        this.segmentFilter = new Set();
        const selectedSegmentsParams = params.getAll(KeywordTrackerPageQueryParams.segment);
        if (selectedSegmentsParams && selectedSegmentsParams.length > 0) {
          for (const id of selectedSegmentsParams) {
            const selectedSegment = segmentIndex.get(parseInt(id));
            if (selectedSegment && selectedSegment.segmentType == SegmentConfigType.KeywordSegment) {
              this.segmentFilter.add(selectedSegment);
            }
          }
          this.selectedSegments.set(Array.from(this.segmentFilter));
        }
        this.keywordTrackerService.setKeywordTrackerData(kwTrackerData);
        this.gridApi?.onFilterChanged();
        this.loadingService.loadingOff();
      });
  }

  changeFilter(filter: string) {
    this.filter = filter;
    this.gridApi?.setGridOption("quickFilterText", filter);
  }

  setSegmentsFilter(segments: SegmentEx[] | undefined) {
    this.segmentFilter = new Set(segments ?? []);
    this.selectedSegments.set(segments ?? []);
    this.gridApi?.onFilterChanged();
    this.updateQueryParams();
  }

  isSegmentFilterPresent(): boolean {
    return this.segmentFilter.size > 0;
  }

  segmentFilterPass(node: IRowNode<KeywordTrackerRow>): boolean {
    if (this.segmentFilter.size === 0) return true;
    for (const segment of this.segmentFilter.values()) {
      if (segment.matchQuery(node.data!.searchTerm)) return true;
    }
    return false;
  }

  navigateToDetail(searchTerm: string) {
    this.router
      .navigate(["/keyword-center/keyword-tracker/" + searchTerm], {
        queryParamsHandling: "merge",
      })
      .then();
  }

  addNewKeyword() {
    this.keywordTrackerService.openAddKwTrackingModal(this.accountId!, this.marketplace!, this.organizationId!);
  }

  deleteSelected() {
    const rows = this.selectedRows();
    if (rows.length === 0) return;

    const toDelete: KeywordTrackerConfig[] = rows.map((r) => ({
      accountId: this.accountId,
      marketplace: this.marketplace,
      organizationId: this.organizationId,
      searchTerm: r.searchTerm,
    }));

    const plural = toDelete.length > 1 ? "s" : "";

    const confirmModal = this.modalServiceV2.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("common.confirm_deletion"),
      data: {
        message: `Do you confirm stop tracking the keyword${plural} selected (${toDelete.length})?`,
      },
    });

    confirmModal
      .afterClosed()
      .pipe(
        switchMap((_) =>
          this.keywordTrackingService.removeKeywordTrackingConfig(
            this.accountId!,
            this.marketplace!,
            this.organizationId!,
            toDelete,
          ),
        ),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("keyword-tracker-page.stopped_tracking_kw", [toDelete.length]),
            this.translocoService.translate("keyword-tracker-page.tracking_keyword_stopped"),
          );
        },
        error: (e) => {
          this.toastrService.error(
            this.translocoService.translate("keyword-tracker-page.error_when_stopping_tracking_kw", [
              toDelete.length,
              e,
            ]),
            this.translocoService.translate("keyword-tracker-page.tracking_keyword_stop_error"),
          );
        },
        complete: () => {
          this.gridApi?.deselectAll();
        },
      });
  }

  stopTracking(searchTerm: string) {
    const ref = this.modalServiceV2.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("stop-keyword-tracking-modal.stop_tracking_term", [searchTerm]),
      data: {
        message: this.translocoService.translate("stop-keyword-tracking-modal.confirmation_stop_tracking", [
          searchTerm,
        ]),
      },
    });

    ref
      .afterClosed()
      .pipe(
        switchMap(() => {
          const toDelete: KeywordTrackerConfig[] = [
            {
              accountId: this.accountId,
              marketplace: this.marketplace,
              organizationId: this.organizationId,
              searchTerm: searchTerm,
            },
          ];

          return this.keywordTrackingService.removeKeywordTrackingConfig(
            this.accountId!,
            this.marketplace!,
            this.organizationId!,
            toDelete,
          );
        }),
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("stop-keyword-tracking-modal.tracking_searchterm_has_been_stopped", [
              searchTerm,
            ]),
            this.translocoService.translate("stop-keyword-tracking-modal.keyword_tracking_stopped"),
          );
        },
        error: (e) => {
          this.toastrService.error(
            this.translocoService.translate(
              "stop-keyword-tracking-modal.error_when_stopping_tracking_this_searchterm",
              [searchTerm, e],
            ),
            this.translocoService.translate("stop-keyword-tracking-modal.keyword_tracking_stop_error"),
          );
        },
      });
  }

  toggleTrackingFrequency(row: KeywordTrackerRow) {
    const frequency =
      row.frequency === KeywordTrackingFrequency.hourly
        ? KeywordTrackingFrequency.daily
        : KeywordTrackingFrequency.hourly;
    this.keywordTrackingService
      .updateKeywordTrackerConfig({
        accountId: this.accountId,
        marketplace: this.marketplace,
        organizationId: this.organizationId,
        searchTerm: row.searchTerm,
        frequency: frequency,
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("keyword-tracker-page.tracking_frequency", [row.searchTerm, frequency]),
            this.translocoService.translate("keyword-tracker-page.tracking_frequency_updated"),
          );
        },
        error: (e) => {
          this.toastrService.error(
            this.translocoService.translate("keyword-tracker-page.error_updating_tracking_frequency", [
              row.searchTerm,
              e,
            ]),
            this.translocoService.translate("keyword-tracker-page.tracking_frequency_update_error"),
          );
        },
      });
  }

  exportAsCsv() {
    const filteredData: KeywordTrackerRow[] = [];
    this.gridApi?.forEachNodeAfterFilter((node) => {
      if (node.data) filteredData.push(node.data);
    });
    this.csvExportService.exportCsv(
      {
        prefix: "keyword_tracker",
        accountGroupName: this.accountMarketplace?.accountGroupName,
        marketplace: this.marketplace,
      },
      filteredData,
      [
        simpleField("searchTerm"),
        simpleField("top10"),
        simpleField("top25"),
        simpleField("frequency", "trackingFrequency"),
        fieldExtractor("topOfSearchRankingsOptimization", (r) => ((r.strategyIds?.length ?? 0) > 0).toString()),
      ],
    );
  }

  private updateQueryParams() {
    const queryParams = {
      [KeywordTrackerPageQueryParams.segment]: Array.from(this.segmentFilter.values()).map((s) => s.segmentId),
    };
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
    });
  }
}
